'use client'

import clsx from 'clsx'
import {
  ComponentProps,
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { sanitizeHtmlId } from '../../helpers/sanitizeId'
import { documentScrollTo } from '../../helpers/scrollTo'
import { useWindowSize } from '../../hooks/useWindowSize'
import Icon from '../Icon'
import LoadingText from '../LoadingText'
import AccordionContent from './AccordionContent'

export type Panel = {
  title: string
  subtitle?: string
  content: ComponentProps<typeof AccordionContent>['content']
}

type AccordionListProps = {
  panels?: Panel[]
  shouldTriggerNavigation?: boolean | null
  initialOpenIndex?: number | null
  shouldScrollToFirstAccordion?: boolean
  loading?: boolean
  loadingLength?: number
  loadingWithSubtitles?: boolean
}

export type AccordionListHandles = {
  setOpenAccordion: (index: number | null) => void
  setOpenAccordionByTitle: (title: string | null) => void
}

const AccordionList = forwardRef<AccordionListHandles, AccordionListProps>(
  (
    {
      panels,
      shouldTriggerNavigation = false,
      initialOpenIndex,
      shouldScrollToFirstAccordion = false,
      loading,
      loadingLength = 3,
      loadingWithSubtitles = false,
    },
    ref,
  ) => {
    const accordionId = useId()
    const isMobile = useWindowSize().isMobile
    const { windowSize } = useWindowSize()

    const [openAccordion, setOpenAccordionState] = useState<number | null>(initialOpenIndex ?? null)
    const [iconSize, setIconSize] = useState<24 | 32>(32)
    const isFirstRender = useRef(true)

    const setOpenAccordion = (index: number | null) => {
      const isPreviouslyOpen = openAccordion != null
      const isSameIndex = index === openAccordion

      if (isSameIndex) {
        setOpenAccordionState(null)
      } else {
        setOpenAccordionState(index)
      }

      if (!shouldTriggerNavigation) return

      const encodedTitle = index !== null ? sanitizeHtmlId(panels?.[index]?.title) : ''
      if (!encodedTitle) return

      if (isSameIndex) {
        history.pushState(null, '', window.location.pathname + window.location.search)
      } else {
        document.getElementById(encodedTitle)?.setAttribute('id', `temp-${encodedTitle}`) // Temporarily change the ID to avoid scrolling
        history.pushState(null, '', `#${encodedTitle}`)
      }

      if (isPreviouslyOpen) {
        // If the accordion was previously open, we scroll to the element after setting the open state
        setTimeout(() => {
          document.getElementById(`temp-${encodedTitle}`)?.setAttribute('id', encodedTitle)
          documentScrollTo(`#${encodedTitle}`, {
            behavior: 'smooth',
            offset: isMobile ? -100 : -200,
          })
        }, 500)
      } else {
        // If it was not previously open, we scroll immediately
        document.getElementById(`temp-${encodedTitle}`)?.setAttribute('id', encodedTitle)
        documentScrollTo(`#${encodedTitle}`, {
          behavior: 'smooth',
          offset: isMobile ? -100 : -200,
        })
      }
    }

    useImperativeHandle(ref, () => ({
      setOpenAccordion,
      setOpenAccordionByTitle: (title: string | null) => {
        if (title == null || panels == null) {
          setOpenAccordion(null)
          return
        }

        const index = panels.findIndex(
          ({ title: panelTitle }) => title === sanitizeHtmlId(panelTitle),
        )
        setOpenAccordion(index !== -1 ? index : null)
      },
    }))

    useEffect(() => {
      setIconSize(windowSize.width >= 1024 ? 32 : 24)
    }, [windowSize.width])

    useEffect(() => {
      if (!shouldTriggerNavigation || !panels) return

      const encodedTitle = window.location.hash.slice(1) ?? ''
      const currentOpen = panels.findIndex(({ title }) => encodedTitle === sanitizeHtmlId(title))

      if (currentOpen !== -1) {
        setOpenAccordionState(currentOpen)

        document.getElementById(encodedTitle)?.scrollIntoView()
      }
    }, [panels, shouldTriggerNavigation])

    useEffect(() => {
      if (openAccordion === null && initialOpenIndex != null) {
        setOpenAccordionState(initialOpenIndex)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialOpenIndex])

    useEffect(() => {
      if (!shouldScrollToFirstAccordion) return
      if (isFirstRender.current) {
        // Skip the effect on the first render when `initialOpenIndex` is set
        isFirstRender.current = false
        return
      }

      if (openAccordion !== null) {
        //scroll to the first accordion item if it's not in view so that the user can see the opened accordion
        const element = document.getElementById(`${accordionId}-heading-0`)
        if (!element) return

        if (element.getBoundingClientRect().top < 0) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openAccordion])

    return (
      <ul style={{ listStyle: 'none' }}>
        {(loading
          ? new Array(loadingLength).fill(null).map(() => ({
              title: '',
              subtitle: loadingWithSubtitles ? '' : undefined,
              content: [],
            }))
          : panels
        )?.map(({ title, subtitle, content }, i, arr) => {
          const headingId = `${accordionId}-heading-${i}`
          const contentId = `${accordionId}-content-${i}`
          const isSelected = i === openAccordion
          const isLast = i === arr.length - 1

          return (
            <li
              key={headingId}
              className={clsx(
                'border border-x-0',
                isLast ? 'border-y-black' : 'border-b-0 border-t-black',
              )}
            >
              {shouldTriggerNavigation && !loading ? (
                <a
                  data-testid='accordion-item'
                  id={sanitizeHtmlId(title)}
                  role='button'
                  aria-expanded={isSelected}
                  aria-controls={contentId}
                  href={`#${sanitizeHtmlId(title)}`}
                  onClick={event => {
                    event.preventDefault()
                    setOpenAccordion(i)
                  }}
                  className='flex w-full items-start justify-between py-6 pr-2 no-underline'
                >
                  <div className='flex flex-col gap-1 overflow-hidden'>
                    <h4
                      className='w-full whitespace-normal break-words text-left text-[18px] leading-[140%] transition-[font-size] lg:text-[28px]'
                      id={headingId}
                    >
                      {title}
                    </h4>

                    <p className='sm w-max truncate text-left text-darkgray'>{subtitle}</p>
                  </div>

                  <span className='shrink-0 pt-0.5 lg:pt-0'>
                    {isSelected ? (
                      <Icon name='MdRemove' size={windowSize.width >= 1024 ? 32 : 24} />
                    ) : (
                      <Icon name='MdAdd' size={windowSize.width >= 1024 ? 32 : 24} />
                    )}
                  </span>
                </a>
              ) : (
                <button
                  id={title}
                  data-testid='accordion-item'
                  aria-expanded={isSelected}
                  aria-controls={contentId}
                  onClick={event => {
                    event.preventDefault()

                    setOpenAccordion(i)
                  }}
                  className='flex w-full items-start justify-between py-6 pr-2'
                  disabled={loading}
                >
                  <div className='flex flex-col gap-1 overflow-hidden'>
                    <h4
                      className='w-full whitespace-normal break-words text-left text-[18px] leading-[140%] transition-[font-size] lg:text-[28px]'
                      id={headingId}
                    >
                      {loading ? <LoadingText textLength={15} /> : title}
                    </h4>

                    <p className='sm w-full truncate text-left text-darkgray'>
                      {loading && subtitle != null ? <LoadingText textLength={15} /> : subtitle}
                    </p>
                  </div>

                  <span className='shrink-0 pt-0.5 lg:pt-0'>
                    {isSelected ? (
                      <Icon name='MdRemove' size={iconSize} />
                    ) : (
                      <Icon name='MdAdd' size={iconSize} />
                    )}
                  </span>
                </button>
              )}

              <AccordionContent
                headingId={headingId}
                contentId={contentId}
                isSelected={isSelected}
                content={content}
              />
            </li>
          )
        })}
      </ul>
    )
  },
)

AccordionList.displayName = 'AccordionList'

export default AccordionList
